import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

const nl = {
  about: {
    eyebrow: "Over ons",
    title: "Hoi, wij zijn Vaxy",
    intro:
      "Vaxy is opgericht door intensivist Dr. Marco Goeijenbier en productbouwer Pim Bruijnes. We helpen mensen grip te houden op hun gezondheid zonder gedoe.",
    storyEyebrow: "Ons verhaal",
    storyTitle: "Van een Amsterdams dakterras naar 200.000+ gebruikers",
    story: [
      "Vaxy begon in 2017 met een simpel idee, ontstaan tijdens een gesprek op een Amsterdams dakterras. Medeoprichters Dr. Marco Goeijenbier en Pim Bruijnes studeerden toen nog.",
      "Marco en Pim raakten in gesprek over reizen, gezondheid en hoe lastig het kan zijn om overzicht te houden. Tijdens de voorbereiding op een reis naar de Filipijnen was het gele boekje nergens te vinden en was onduidelijk welke vaccinaties nog nodig waren.",
      "Tegelijkertijd werkte Marco aan zijn promotieonderzoek naar infectieziekten, waarbij hij infectierisico's en vaccinatieadviezen per land zorgvuldig bijhield. Al snel ontstond het besef dat die waardevolle informatie veel toegankelijker en overzichtelijker kon.",
      "Dat inzicht leidde tot een eenvoudige gedachte: deze informatie moet digitaal, duidelijk en altijd beschikbaar zijn. Zo ontstond de app Vaccinatieboekje, nu bekend als Vaxy.",
      "Tijdens de pandemie in 2019 ontdekten steeds meer gebruikers het gemak van een digitaal vaccinatieoverzicht. Sindsdien groeit Vaxy als platform voor overzicht en herinneringen, ook voor de prikken die horen bij ouder worden. Marco bewaakt nog steeds de medische inhoud, vanuit de dagelijkse praktijk als intensivist.",
    ],
    teamEyebrow: "Het team",
    teamTitle: "De mensen achter Vaxy",
    linkedinLabel: "LinkedIn-profiel",
    team: [
      {
        name: "Dr. Marco Goeijenbier",
        title: "Medisch expert",
        role: "Medeoprichter · Intensivist",
        bio: "Marco is medeoprichter van Vaxy, arts en onderzoeker met een sterke focus op infectieziekten en preventieve zorg. Vanuit zijn medische en wetenschappelijke achtergrond vertaalt hij infectierisico's naar praktische toepassingen. Bij Vaxy bewaakt Marco de medische inhoud, zodat informatie klopt, actueel is en begrijpelijk blijft.",
        linkedin: "https://www.linkedin.com/in/marco-goeijenbier-0b3a5927/",
        img: "/images/team/marco.webp",
      },
      {
        name: "Pim Bruijnes",
        title: "MSc.",
        role: "Medeoprichter · Product en technologie",
        bio: "Pim is medeoprichter van Vaxy, ondernemer en productontwikkelaar met een achtergrond in technologie en digitale platforms. Met zijn ervaring in gebruiksvriendelijke web- en mobiele apps richt hij zich op het vertalen van complexe informatie naar simpele, betrouwbare producten. Bij Vaxy is Pim verantwoordelijk voor productvisie, technologie en gebruikservaring.",
        linkedin: "https://www.linkedin.com/in/pim-bruijnes-a4857122/",
        img: "/images/team/pim.webp",
      },
    ],
  },
  media: {
    eyebrow: "Media",
    title: "Vaxy in de media",
    intro:
      "Bekijk hoe Vaxy de manier verandert waarop mensen hun vaccinaties beheren.",
    items: [
      {
        kind: "In het nieuws · EVA (AVROTROS) · 05-05-2026",
        source: "EVA — AVROTROS",
        title:
          'Doden op Nederlands cruiseschip door hantavirus: "Knaagdieren dragen dit virus bij zich"',
        description:
          "Op een Nederlands schip heeft het zeldzame Hantavirus voor meerdere doden gezorgd. Marco Goeijenbier is intensivist in het Spaarne Gasthuis en praat ons bij.",
        cta: "Lees het artikel",
        href: "https://eva.avrotros.nl/artikel/doden-op-nederlands-cruiseschip-door-hantavirus-knaagdieren-dragen-dit-virus-bij-zich-1417",
        imgAlt: "Marco Goeijenbier bij EVA AVROTROS",
      },
      {
        kind: "Video · Vaxy App",
        source: "YouTube",
        title: "Vaxy — Jouw digitale vaccinatieboekje",
        description:
          "Nooit meer zoeken naar je gele boekje. Vaxy brengt al jouw vaccinatiegegevens samen in één veilige app voor jezelf, je kinderen en je reisvaccins. Bekijk hoe het werkt.",
        cta: "Bekijk de video",
        href: "https://www.youtube.com/embed/EtvYcOugFzI",
        imgAlt: "Vaxy video",
      },
    ],
  },
  community: {
    eyebrow: "Community",
    title: "Samen voor vaccinatie",
    intro:
      "Sluit je aan bij een groeiende community van mensen die geloven in de wetenschap achter vaccins en de kracht van preventie. Samen beschermen we onszelf, onze gezinnen en onze omgeving.",
    cta: "Download Vaxy gratis",
    proof: "200.000+ leden wereldwijd",
    valuesEyebrow: "Waar we voor staan",
    valuesTitle: "Onze gedeelde waarden",
    values: [
      {
        title: "Wetenschappelijk onderbouwd",
        description:
          "We staan achter het bewijs. Vaccins zijn een van de grootste prestaties van de moderne geneeskunde.",
      },
      {
        title: "Zorg voor elkaar",
        description:
          "Vaccinatie beschermt niet alleen jezelf, maar ook mensen die niet gevaccineerd kunnen worden: pasgeborenen, ouderen en mensen met een kwetsbare afweer.",
      },
      {
        title: "Wereldwijde solidariteit",
        description:
          "Groepsimmuniteit is iets wat we samen doen. Elke vaccinatie draagt bij aan een gezondere wereld.",
      },
      {
        title: "Inclusieve community",
        description:
          "Ouders, zorgverleners, reizigers, onderzoekers: iedereen die gelooft in preventie is welkom.",
      },
    ],
    storiesEyebrow: "Stemmen uit de community",
    storiesTitle: "Echte verhalen, echte impact",
    testimonials: [
      {
        name: "Lena R.",
        role: "Moeder van drie",
        quote:
          "Vaxy maakt het zo makkelijk om de vaccinaties van mijn kinderen bij te houden. Ik weet zeker dat ze beschermd zijn.",
      },
      {
        name: "Dr. Ayaan K.",
        role: "Huisarts",
        quote:
          "Ik raad Vaxy aan iedere patiënt aan. Een helder vaccinatieoverzicht betekent betere zorg en minder gemiste prikken.",
      },
      {
        name: "Sophie M.",
        role: "Frequente reiziger",
        quote:
          "Reizen naar tropische bestemmingen geeft veel minder stress als ik precies weet welke vaccinaties ik heb en wanneer ze verlopen.",
      },
    ],
    movementTitle: "Doe mee met de beweging",
    movementText:
      "Download Vaxy, houd je vaccinaties bij en help bouwen aan een gezondere, beter beschermde wereld, prik voor prik.",
  },
  app: {
    eyebrow: "De app",
    title: "Je digitale vaccinatieboekje",
    intro:
      "Vaxy bewaart al je vaccinaties en boosters in één app, ook die van je hele gezin. Registreer prikken, ontvang herinneringen en boek reisvaccinaties.",
    heroImageAlt:
      "Gezond ouder stel op een duinpad, met de Vaxy-app en een herinnering voor de griepprik",
    screens: [
      {
        headline: "Zorgeloos Gezond.",
        subline: "Bescherm jezelf.\nBescherm elkaar.",
      },
      {
        headline: "Alle vaccinaties\nop één plek",
        subline: "Je complete digitale vaccinatieboekje, altijd bij de hand.",
      },
      {
        headline: "Zorg voor\nhet hele gezin",
        subline: "Voeg gezinsleden toe en beheer ieders vaccinaties samen.",
      },
      {
        headline: "Ontvang booster-\nmeldingen",
        subline: "Krijg een seintje voordat vaccinaties verlopen.",
      },
      {
        headline: "Bespaar tijd en\nimporteer automatisch",
        subline:
          "Scan je gele boekje of RIVM-documenten, Vaxy vult de rest aan.",
      },
    ],
    insideEyebrow: "Wat zit erin",
    insideTitle: "Alles in één app",
    fullListEyebrow: "Volledige functielijst",
    fullListTitle: "Alles wat Vaxy kan",
    features: [
      {
        title: "Reisvaccinatieplanner",
        description:
          "Selecteer land, reizigers en vertrekdatum en krijg persoonlijk vaccinatieadvies op basis van LCR-richtlijnen.",
      },
      {
        title: "Afspraken boeken",
        description:
          "Boek direct bij Travel Doctor en Vaccinatiepunt. Filter op locatie, beschikbaarheid of last-minute plekken.",
      },
      {
        title: "Digitaal vaccinatieboekje",
        description:
          "Bewaar alle prikken per persoon, met data en productnamen, altijd bij de hand.",
      },
      {
        title: "Gezin en gedeelde toegang",
        description:
          "Beheer vaccinaties voor kinderen, nodig mede-ouders uit en deel veilige PDF-overzichten.",
      },
      {
        title: "GriepAlert",
        description:
          "Volg griepactiviteit in jouw regio, meld klachten en krijg een seintje als het aantal meldingen stijgt.",
      },
      {
        title: "Slim documenten scannen",
        description:
          "Scan vaccinatiekaarten of certificaten. Vaxy herkent en importeert de gegevens automatisch.",
      },
      {
        title: "Vaccinatie-PDF delen",
        description:
          "Download beveiligde PDF's en deel ze met zorgverleners, scholen of werkgevers.",
      },
      {
        title: "Vaccinatieregistraties uploaden",
        description:
          "Foto's van je gele boekje, RIVM-kaart of andere bewijzen. Vaxy bewaart ze bij de juiste persoon.",
      },
      {
        title: "Boostersherinneringen",
        description:
          "Krijg een seintje wanneer een herhaalprik nodig is, zodat je bescherming niet stilletjes verloopt.",
      },
      {
        title: "Vermeld in de GGD App Store",
        description:
          "Vaxy staat in de officiële GGD App Store en wordt vertrouwd door gezondheidsorganisaties in Nederland.",
      },
    ],
    downloadTitle: "Download Vaxy gratis",
    downloadText:
      "Beschikbaar op iOS en Android. Start vandaag met het beheren van de vaccinaties van je gezin, zonder gedoe.",
  },
  travel: {
    eyebrow: "Reisgezondheid",
    title: "Veilig reizen, slim vaccineren",
    intro:
      "Elke bestemming heeft eigen gezondheidsrisico's. Ontdek welke vaccins je nodig hebt voordat je vertrekt en bewaar je gegevens in Vaxy.",
    tips: [
      {
        title: "Plan 4-6 weken vooruit",
        desc: "Sommige vaccins vragen meerdere doses verspreid over weken. Boek je reisconsult op tijd.",
      },
      {
        title: "Houd je overzicht actueel",
        desc: "Neem je vaccinatiebewijs mee. Sommige landen vragen bewijs aan de grens.",
      },
      {
        title: "Check inreisregels",
        desc: "Regels veranderen. Controleer altijd de laatste eisen bij officiële gezondheidsinstanties.",
      },
      {
        title: "Routinevaccins eerst",
        desc: "Zorg dat BMR, DTP, griep en COVID-19 op orde zijn voordat je reisvaccins toevoegt.",
      },
    ],
    regionsTitle: "Vaccins per bestemming",
    vaccineLabel: "Veelvoorkomende adviezen",
    allDestinationsTitle: "Alle bestemmingen",
    regions: [
      {
        slug: "zuidoost-azie",
        name: "Zuidoost-Azië",
        vaccines: ["Hepatitis A", "Buiktyfus", "Japanse encefalitis", "Rabiës"],
        note: "Malariaprofylaxe wordt aangeraden voor landelijke gebieden.",
      },
      {
        slug: "sub-sahara-afrika",
        name: "Sub-Sahara Afrika",
        vaccines: [
          "Gele koorts",
          "Hepatitis A & B",
          "Buiktyfus",
          "Meningokokken",
        ],
        note: "Een gelekoortscertificaat is vaak verplicht voor toegang.",
      },
      {
        slug: "zuid-amerika",
        name: "Zuid-Amerika",
        vaccines: ["Gele koorts", "Hepatitis A", "Buiktyfus", "Rabiës"],
        note: "Voor het Amazonegebied gelden extra voorzorgsmaatregelen.",
      },
      {
        slug: "midden-oosten",
        name: "Midden-Oosten",
        vaccines: [
          "Hepatitis A & B",
          "Buiktyfus",
          "Meningokokken (Hadj/Umrah)",
        ],
        note: "Meningokokkenvaccinatie is verplicht voor pelgrims.",
      },
      {
        slug: "europa-noord-amerika",
        name: "Europa & Noord-Amerika",
        vaccines: ["Routinevaccins actueel", "Griep", "COVID-19"],
        note: "Controleer of je basisvaccinaties nog up-to-date zijn.",
      },
      {
        slug: "zuid-centraal-azie",
        name: "Zuid- & Centraal-Azië",
        vaccines: ["Hepatitis A", "Buiktyfus", "Poliobooster", "Rabiës"],
        note: "Een poliobooster kan verplicht zijn voor Pakistan en Afghanistan.",
      },
      {
        slug: "cariben",
        name: "Cariben & Midden-Amerika",
        vaccines: ["Hepatitis A", "Buiktyfus", "DTP"],
        note: "Dengue en andere muggenziekten komen veel voor. Gele koorts speelt vooral bij reizen via Zuid-Amerika.",
      },
      {
        slug: "oost-azie",
        name: "Oost-Azië",
        vaccines: ["DTP", "Hepatitis A", "Japanse encefalitis"],
        note: "Voor de meeste steden volstaan basisvaccinaties. Japanse encefalitis geldt vooral bij langer verblijf op het platteland.",
      },
      {
        slug: "oceanie",
        name: "Oceanië",
        vaccines: ["DTP", "Hepatitis A", "Routinevaccins actueel"],
        note: "Australië en Nieuw-Zeeland vragen vooral actuele basisvaccinaties. Op veel eilanden telt extra bescherming tegen hepatitis A.",
      },
    ],
    destination: {
      title: "Vaccinaties {name}",
      lead: "Voor {name} adviseert het LCR onder andere {vaccines}.",
      leadEmpty: "Bekijk hieronder het LCR-advies voor een reis naar {name}.",
      source: "Bron: Landelijk Coördinatiecentrum Reizigersadvisering (LCR)",
      sourceLink: "Open de LCR-pagina",
      updated: "Laatst bijgewerkt {date}",
      sections: {
        general: "Algemeen advies",
        conditional: "Afhankelijk van je reis",
        other: "Overige aandachtspunten",
      },
      faqTitle: "Veelgestelde vragen",
      faqRequiredQ: "Welke vaccinaties zijn verplicht voor {name}?",
      faqRequiredYes:
        "Het LCR noemt voor {name} vaccinatie(s) die verplicht kunnen zijn voor inreis: {vaccines}. Controleer altijd de laatste inreisregels bij een reizigersvaccinatiebureau.",
      faqRequiredNo:
        "Voor {name} noemt het LCR geen vaccinatie die verplicht is voor inreis. Aanbevolen zijn onder andere {vaccines}.",
      faqRecommendedQ: "Welke vaccinaties worden aanbevolen voor {name}?",
      faqRecommendedA:
        "Volgens het LCR horen bij een reis naar {name} onder andere: {vaccines}. Of jij ze nodig hebt hangt af van je gezondheid, verblijfsduur en activiteiten.",
      faqWhenQ: "Wanneer moet ik reisvaccinaties halen?",
      faqWhenA:
        "Plan 4 tot 6 weken voor vertrek een afspraak bij een reizigersvaccinatiebureau. Sommige vaccins vragen meerdere doses.",
      siblingsTitle: "Andere landen in {region}",
      countriesTitle: "Landen in {region}",
      browseAll: "Alle bestemmingen",
      relatedGuide: "Lees de gids over reisvaccinaties",
      relatedApp: "Bekijk wat Vaxy kan",
    },
    diseasesTitle: "Infectieziekten en reisvaccins",
    diseasesIntro:
      "Bekijk per ziekte waar het LCR vaccinatie of extra voorzorg adviseert, met links naar het advies per land.",
    disease: {
      title: "{name}",
      lead: "Het LCR noemt {name} in het reisadvies van {count} bestemmingen.",
      aboutTitle: "Wat is {name}?",
      lcrAboutLabel: "Volgens het LCR (Nederlands reisadvies)",
      whoTitle: "Volgens de WHO",
      rvpTitle: "Volgens het Rijksvaccinatieprogramma",
      whoLink: "Open de WHO-pagina",
      rvpLink: "Open de RVP-pagina",
      rvpHub:
        "Kinder- en tienerprikken in Nederland staan op het Rijksvaccinatieprogramma. Dat is iets anders dan het LCR-reisadvies.",
      rvpHubLink: "Rijksvaccinatieprogramma.nl",
      faqWhatQ: "Wat is {name}?",
      faqWhereQ: "Voor welke landen is {name} relevant?",
      faqWhereA:
        "Het LCR noemt {name} voor {count} bestemmingen, waaronder {examples}. Open een land voor het volledige advies.",
      faqRequiredQ: "Is een vaccinatie tegen {name} verplicht?",
      faqRequiredYes:
        "Voor sommige bestemmingen kan {name} verplicht zijn voor inreis. Controleer altijd het landadvies en de laatste inreisregels.",
      faqRequiredNo:
        "Het LCR noemt {name} vooral als advies, niet als verplichte inreisvaccinatie. Of jij het nodig hebt hangt af van je reis.",
    },
    ctaTitle: "Bewaar al je reisvaccinaties in Vaxy",
    ctaText:
      "Krijg herinneringen, draag een digitaal bewijs bij je en deel gegevens met reisgenoten, allemaal in één app.",
    cta: "Download Vaxy gratis",
  },
  blog: {
    eyebrow: "Artikelen",
    title: "Gezondheid, reizen en vaccinaties helder uitgelegd",
    intro:
      "Praktische gidsen en verhalen om jou en je gezin beschermd te houden.",
    readArticle: "Lees artikel",
    allArticles: "Alle artikelen",
    backToBlog: "Terug naar artikelen",
    by: "Door",
  },
  contact: {
    eyebrow: "Contact",
    title: "Vragen? We helpen je graag verder",
    intro:
      "Heb je een vraag over Vaxy, je account of het digitale vaccinatieboekje? Mail ons en we reageren zo snel mogelijk.",
    generalLabel: "Algemene vragen",
    legacyLabel: "Vragen over Vaccinatieboekje",
    infoEmail: "info@vaxy.app",
    legacyEmail: "info@vaccinatieboekje.com",
  },
  deleteAccount: {
    eyebrow: "Account",
    title: "Account verwijderen",
    intro:
      "Je kunt je Vaxy-account zelf verwijderen in de app, of een verzoek indienen als dat niet lukt.",
    stepsTitle: "Verwijderen in de app",
    steps: [
      "Open de Vaxy-app en log in op je account.",
      "Ga naar Account.",
      "Kies Account verwijderen.",
      "Bevestig je keuze. Je account en gekoppelde persoonsgegevens worden permanent verwijderd.",
    ],
    warning:
      "Het verwijderen van je account is permanent en kan niet ongedaan worden gemaakt. Alle gegevens die aan je account zijn gekoppeld, worden eveneens verwijderd.",
    requestTitle: "Verzoek indienen",
    requestIntro:
      "Lukt het niet via de app? Stuur ons een verzoek. Gebruik het e-mailadres van je Vaxy-account, zodat we je kunnen vinden.",
    emailLabel: "E-mailadres van je account",
    emailPlaceholder: "naam@voorbeeld.nl",
    messageLabel: "Toelichting (optioneel)",
    messagePlaceholder: "Bijvoorbeeld: ik heb geen toegang meer tot de app.",
    submitLabel: "Verzoek versturen",
    requestHint:
      "Je e-mailapp opent met een vooringevuld bericht aan info@vaxy.app. We verwerken verzoeken zo snel mogelijk.",
    supportEmail: "info@vaxy.app",
    mailSubject: "Verzoek tot accountverwijdering",
    mailBodyIntro:
      "Ik verzoek Vaxy om mijn account en bijbehorende persoonsgegevens te verwijderen.",
  },
};

export type PagesCopy = typeof nl;

const en: PagesCopy = {
  about: {
    ...nl.about,
    eyebrow: "About us",
    title: "Hi, we are Vaxy",
    intro:
      "Vaxy was founded by intensivist Dr. Marco Goeijenbier and product builder Pim Bruijnes. We help people stay in control of their health without the hassle.",
    storyEyebrow: "Our story",
    storyTitle: "From an Amsterdam rooftop to 200,000+ users",
    story: [
      "Vaxy started in 2017 with a simple idea, born during a conversation on an Amsterdam rooftop terrace. Co-founders Dr. Marco Goeijenbier and Pim Bruijnes were still students then.",
      "Marco and Pim got talking about travel, health and how difficult it can be to keep a clear overview. While preparing for a trip to the Philippines, the Yellow Booklet was nowhere to be found and it was unclear which vaccinations were still needed.",
      "At the same time, Marco was working on his doctoral research into infectious diseases, carefully tracking infection risks and vaccination requirements per country. The realisation quickly emerged that this valuable information could be made far more accessible and organised.",
      "That insight led to a simple thought: this information should be digital, clear and always available. And so the app Vaccinatieboekje, now known as Vaxy, was born.",
      "During the 2019 pandemic, more and more users discovered the convenience of a digital vaccination overview. Since then, Vaxy has grown as a platform for records and reminders, including the jabs that come with getting older. Marco still oversees the medical content, from everyday practice as an intensivist.",
    ],
    teamEyebrow: "The team",
    teamTitle: "The people behind Vaxy",
    linkedinLabel: "LinkedIn profile",
    team: [
      {
        ...nl.about.team[0],
        title: "Medical Expert",
        role: "Co-founder · Intensivist",
        bio: "Marco is co-founder of Vaxy, a physician and researcher with a strong focus on infectious diseases and preventive care. From his medical and scientific background, he translates infection risks into practical applications. At Vaxy, Marco oversees the medical content, ensuring information is accurate, up-to-date and easy to understand.",
      },
      {
        ...nl.about.team[1],
        title: "MSc.",
        role: "Co-founder · Product & technology",
        bio: "Pim is co-founder of Vaxy, an entrepreneur and product developer with a background in technology and digital platforms. Drawing on his experience building user-friendly web and mobile applications, he focuses on translating complex information into simple, reliable products. At Vaxy, Pim is responsible for product vision, technology and user experience.",
      },
    ],
  },
  media: {
    eyebrow: "Media",
    title: "Vaxy in the media",
    intro:
      "Watch how Vaxy is changing the way people manage their vaccinations.",
    items: [
      {
        ...nl.media.items[0],
        description:
          "A rare hantavirus caused several deaths on a Dutch ship. Marco Goeijenbier, intensivist at Spaarne Gasthuis, explains what happened.",
        cta: "Read the article",
        imgAlt: "Marco Goeijenbier at EVA AVROTROS",
      },
      {
        ...nl.media.items[1],
        description:
          "No more searching for your yellow booklet. Vaxy brings all your vaccination details together in one secure app for you, your children and your travel vaccines. See how it works.",
        cta: "Watch the video",
      },
    ],
  },
  community: {
    ...nl.community,
    title: "United for vaccination",
    intro:
      "Join a growing community of people who believe in the science of vaccines and the power of prevention. Together, we protect ourselves, our families and our communities.",
    cta: "Download Vaxy free",
    proof: "200,000+ members worldwide",
    valuesEyebrow: "What we stand for",
    valuesTitle: "Our shared values",
    values: [
      {
        title: "Science-based",
        description:
          "We stand by the evidence. Vaccines are one of the greatest achievements in modern medicine.",
      },
      {
        title: "Caring for others",
        description:
          "Vaccination protects not just you, but also those who cannot be vaccinated: newborns, the elderly and the immunocompromised.",
      },
      {
        title: "Global solidarity",
        description:
          "Herd immunity is a collective act. Every vaccination contributes to a healthier world for everyone.",
      },
      {
        title: "Inclusive community",
        description:
          "Parents, healthcare workers, travellers, researchers: everyone who believes in prevention is welcome here.",
      },
    ],
    storiesEyebrow: "Voices from the community",
    storiesTitle: "Real stories, real impact",
    testimonials: [
      {
        name: "Lena R.",
        role: "Mother of three",
        quote:
          "Vaxy made it so easy to keep track of my children's vaccinations. I feel confident knowing they're protected.",
      },
      {
        name: "Dr. Ayaan K.",
        role: "General Practitioner",
        quote:
          "I recommend Vaxy to every patient. Having a clear vaccination record means better care and fewer missed doses.",
      },
      {
        name: "Sophie M.",
        role: "Frequent traveller",
        quote:
          "Travelling to tropical destinations is so much less stressful when I know exactly which vaccinations I have and when they expire.",
      },
    ],
    movementTitle: "Be part of the movement",
    movementText:
      "Download Vaxy, track your vaccinations and help build a healthier, more protected world, one dose at a time.",
  },
  app: {
    ...nl.app,
    eyebrow: "The app",
    title: "Your digital vaccination booklet",
    intro:
      "Vaxy stores all your vaccinations and boosters in one app, including your whole family's. Register jabs, receive reminders and book travel vaccines.",
    heroImageAlt:
      "Healthy older couple on a coastal path, with the Vaxy app and a flu-shot reminder",
    screens: [
      {
        headline: "Healthy without worry.",
        subline: "Protect yourself.\nProtect each other.",
      },
      {
        headline: "Manage all vaccinations\nin one place",
        subline: "Your complete digital vaccination record, always at hand.",
      },
      {
        headline: "Care for\nthe whole family",
        subline: "Add family members and manage everyone's vaccines together.",
      },
      {
        headline: "Receive booster\nnotifications",
        subline:
          "Get alerts before vaccinations expire so you're never caught off guard.",
      },
      {
        headline: "Save time and\nimport automatically",
        subline:
          "Scan your Yellow Booklet or RIVM documents, Vaxy fills in the rest.",
      },
    ],
    insideEyebrow: "What's inside",
    insideTitle: "Everything in one app",
    fullListEyebrow: "Full feature list",
    fullListTitle: "Everything Vaxy can do",
    features: [
      {
        title: "Travel vaccination planner",
        description:
          "Select country, travellers and departure date and get personalised vaccine advice based on LCR (Dutch travel health) guidance.",
      },
      {
        title: "Book appointments",
        description:
          "Book directly at Travel Doctor and Vaccinatiepunt clinics. Filter by location, availability or last-minute slots.",
      },
      {
        title: "Digital vaccination booklet",
        description:
          "Store every dose per person, with dates and product names, always at hand.",
      },
      {
        title: "Family & shared access",
        description:
          "Manage vaccines for children, invite co-parents and share secure PDF summaries.",
      },
      {
        title: "GriepAlert",
        description:
          "Track flu activity in your region, report symptoms and get notified when reports go up.",
      },
      {
        title: "Smart document scanning",
        description:
          "Scan vaccine cards or certificates. Vaxy automatically recognises and imports the data.",
      },
      {
        title: "Share vaccination PDF",
        description:
          "Download secured PDFs and share them with healthcare providers, schools or employers.",
      },
      {
        title: "Upload vaccination records",
        description:
          "Photos of your Yellow Booklet, RIVM card or other proof. Vaxy stores them with the right person.",
      },
      {
        title: "Booster reminders",
        description:
          "Get an alert when a repeat dose is due, so protection does not quietly expire.",
      },
      {
        title: "GGD App Store listed",
        description:
          "Vaxy is included in the official GGD App Store and trusted by health organisations in the Netherlands.",
      },
    ],
    downloadTitle: "Download Vaxy for free",
    downloadText:
      "Available on iOS and Android. Start managing your family's vaccinations today without sign-up hassle.",
  },
  travel: {
    ...nl.travel,
    eyebrow: "Travel Health",
    title: "Travel safe, vaccinate smart",
    intro:
      "Every destination comes with its own health risks. Discover which vaccines you need before you go and store your records in Vaxy.",
    tips: [
      {
        title: "Plan 4-6 weeks ahead",
        desc: "Some vaccines require multiple doses spread over weeks. Book your travel health appointment early.",
      },
      {
        title: "Keep your record up to date",
        desc: "Carry your vaccination certificate. Some countries require proof at the border.",
      },
      {
        title: "Check entry requirements",
        desc: "Requirements change. Always verify the latest rules with official health authorities.",
      },
      {
        title: "Routine vaccines first",
        desc: "Make sure MMR, Tdap, flu and COVID-19 are current before adding travel-specific vaccines.",
      },
    ],
    regionsTitle: "Vaccines by destination",
    vaccineLabel: "Common recommendations",
    allDestinationsTitle: "All destinations",
    regions: [
      {
        slug: "zuidoost-azie",
        name: "Southeast Asia",
        vaccines: ["Hepatitis A", "Typhoid", "Japanese Encephalitis", "Rabies"],
        note: "Malaria prophylaxis recommended for rural areas.",
      },
      {
        slug: "sub-sahara-afrika",
        name: "Sub-Saharan Africa",
        vaccines: [
          "Yellow Fever",
          "Hepatitis A & B",
          "Typhoid",
          "Meningococcal",
        ],
        note: "Yellow Fever certificate often required for entry.",
      },
      {
        slug: "zuid-amerika",
        name: "South America",
        vaccines: ["Yellow Fever", "Hepatitis A", "Typhoid", "Rabies"],
        note: "Amazon region requires additional precautions.",
      },
      {
        slug: "midden-oosten",
        name: "Middle East",
        vaccines: ["Hepatitis A & B", "Typhoid", "Meningococcal (Hajj/Umrah)"],
        note: "Meningococcal vaccine mandatory for pilgrims.",
      },
      {
        slug: "europa-noord-amerika",
        name: "Europe & North America",
        vaccines: ["Routine vaccines up-to-date", "Influenza", "COVID-19"],
        note: "Ensure routine immunizations are current.",
      },
      {
        slug: "zuid-centraal-azie",
        name: "South & Central Asia",
        vaccines: ["Hepatitis A", "Typhoid", "Polio booster", "Rabies"],
        note: "Polio booster may be required for Pakistan and Afghanistan.",
      },
      {
        slug: "cariben",
        name: "Caribbean & Central America",
        vaccines: ["Hepatitis A", "Typhoid", "DTP"],
        note: "Dengue and other mosquito-borne diseases are common. Yellow fever matters mainly if you travel via South America.",
      },
      {
        slug: "oost-azie",
        name: "East Asia",
        vaccines: ["DTP", "Hepatitis A", "Japanese encephalitis"],
        note: "Routine vaccines cover most city trips. Japanese encephalitis mainly applies to longer stays in rural areas.",
      },
      {
        slug: "oceanie",
        name: "Oceania",
        vaccines: ["DTP", "Hepatitis A", "Routine vaccines up-to-date"],
        note: "Australia and New Zealand mainly need current routine vaccines. Many islands add hepatitis A protection.",
      },
    ],
    destination: {
      title: "Vaccinations {name}",
      lead: "For {name}, LCR recommends {vaccines}, among others.",
      leadEmpty: "See the LCR advice below for travel to {name}.",
      source:
        "Source: National Coordination Centre for Travellers' Health (LCR)",
      sourceLink: "Open the LCR page",
      updated: "Last updated {date}",
      sections: {
        general: "General advice",
        conditional: "Depends on your trip",
        other: "Other points to watch",
      },
      faqTitle: "Frequently asked questions",
      faqRequiredQ: "Which vaccinations are required for {name}?",
      faqRequiredYes:
        "LCR lists vaccination(s) that may be required for entry to {name}: {vaccines}. Always check the latest entry rules with a travel clinic.",
      faqRequiredNo:
        "LCR does not list a vaccination that is required for entry to {name}. Recommended vaccines include {vaccines}.",
      faqRecommendedQ: "Which vaccinations are recommended for {name}?",
      faqRecommendedA:
        "According to LCR, travel to {name} typically includes: {vaccines}. Whether you need them depends on your health, trip length and activities.",
      faqWhenQ: "When should I get travel vaccinations?",
      faqWhenA:
        "Book a travel clinic appointment 4 to 6 weeks before departure. Some vaccines need more than one dose.",
      siblingsTitle: "Other countries in {region}",
      countriesTitle: "Countries in {region}",
      browseAll: "All destinations",
      relatedGuide: "Read the travel vaccinations guide",
      relatedApp: "See what Vaxy can do",
    },
    diseasesTitle: "Infectious diseases and travel vaccines",
    diseasesIntro:
      "See where LCR recommends vaccination or extra precautions per disease, with links to advice by country.",
    disease: {
      title: "{name}",
      lead: "LCR mentions {name} in travel advice for {count} destinations.",
      aboutTitle: "What is {name}?",
      lcrAboutLabel: "According to LCR (Dutch travel advice)",
      whoTitle: "According to WHO",
      rvpTitle: "According to the Dutch national immunisation programme",
      whoLink: "Open the WHO page",
      rvpLink: "Open the RVP page",
      rvpHub:
        "Childhood and teenage vaccines in the Netherlands are on the national immunisation programme. That is separate from LCR travel advice.",
      rvpHubLink: "Rijksvaccinatieprogramma.nl",
      faqWhatQ: "What is {name}?",
      faqWhereQ: "Which countries is {name} relevant for?",
      faqWhereA:
        "LCR mentions {name} for {count} destinations, including {examples}. Open a country for the full advice.",
      faqRequiredQ: "Is vaccination against {name} required?",
      faqRequiredYes:
        "For some destinations {name} may be required for entry. Always check the country page and the latest entry rules.",
      faqRequiredNo:
        "LCR lists {name} mainly as advice, not as a required entry vaccination. Whether you need it depends on your trip.",
    },
    ctaTitle: "Store all your travel vaccines in Vaxy",
    ctaText:
      "Get reminders, carry a digital certificate and share records with your travel companions, all in one app.",
    cta: "Get Vaxy free",
  },
  blog: {
    eyebrow: "Articles",
    title: "Health, travel and vaccinations made clear",
    intro:
      "Practical guides and stories to keep you and your family protected.",
    readArticle: "Read article",
    allArticles: "All articles",
    backToBlog: "Back to articles",
    by: "By",
  },
  contact: {
    eyebrow: "Contact",
    title: "Questions? We are happy to help",
    intro:
      "Have a question about Vaxy, your account or the digital vaccination booklet? Email us and we will get back to you as soon as possible.",
    generalLabel: "General questions",
    legacyLabel: "Questions about Vaccinatieboekje",
    infoEmail: "info@vaxy.app",
    legacyEmail: "info@vaccinatieboekje.com",
  },
  deleteAccount: {
    eyebrow: "Account",
    title: "Delete your account",
    intro:
      "You can delete your Vaxy account yourself in the app, or submit a request if that is not possible.",
    stepsTitle: "Delete in the app",
    steps: [
      "Open the Vaxy app and sign in to your account.",
      "Go to Account.",
      "Choose Delete account.",
      "Confirm your choice. Your account and linked personal data will be permanently deleted.",
    ],
    warning:
      "Deleting your account is permanent and cannot be undone. All data linked to your account will also be deleted.",
    requestTitle: "Submit a request",
    requestIntro:
      "Cannot delete via the app? Send us a request. Use the email address of your Vaxy account so we can find you.",
    emailLabel: "Email address of your account",
    emailPlaceholder: "name@example.com",
    messageLabel: "Additional details (optional)",
    messagePlaceholder: "For example: I no longer have access to the app.",
    submitLabel: "Send request",
    requestHint:
      "Your email app opens with a prefilled message to info@vaxy.app. We process requests as soon as possible.",
    supportEmail: "info@vaxy.app",
    mailSubject: "Account deletion request",
    mailBodyIntro:
      "I request that Vaxy delete my account and related personal data.",
  },
};

const de: PagesCopy = {
  ...en,
  about: {
    ...en.about,
    eyebrow: "Über uns",
    title: "Hallo, wir sind Vaxy",
    intro:
      "Vaxy wurde von Intensivmediziner Dr. Marco Goeijenbier und Produktentwickler Pim Bruijnes gegründet. Wir helfen Menschen, ihre Gesundheit ohne Aufwand im Blick zu behalten.",
    storyEyebrow: "Unsere Geschichte",
    storyTitle: "Von einer Amsterdamer Dachterrasse zu 200.000+ Nutzern",
    teamEyebrow: "Das Team",
    teamTitle: "Die Menschen hinter Vaxy",
    linkedinLabel: "LinkedIn-Profil",
    team: [
      {
        ...en.about.team[0],
        title: "Medizinischer Experte",
        role: "Mitgründer · Intensivmediziner",
      },
      { ...en.about.team[1], role: "Mitgründer · Produkt und Technologie" },
    ],
  },
  media: {
    ...en.media,
    title: "Vaxy in den Medien",
    intro:
      "Sehen Sie, wie Vaxy verändert, wie Menschen ihre Impfungen verwalten.",
    items: [
      {
        ...en.media.items[0],
        description:
          "Auf einem niederländischen Schiff führte ein seltenes Hantavirus zu mehreren Todesfällen. Marco Goeijenbier ordnet die Situation ein.",
        cta: "Artikel lesen",
      },
      {
        ...en.media.items[1],
        description:
          "Nie wieder nach dem gelben Impfheft suchen. Vaxy bringt alle Impfdaten in einer sicheren App zusammen: für Sie, Ihre Kinder und Reiseimpfungen.",
        cta: "Video ansehen",
      },
    ],
  },
  community: {
    ...en.community,
    title: "Gemeinsam für Impfungen",
    intro:
      "Werden Sie Teil einer wachsenden Community, die an die Wissenschaft hinter Impfstoffen und die Kraft der Vorbeugung glaubt. Gemeinsam schützen wir uns, unsere Familien und unsere Umgebung.",
    cta: "Vaxy kostenlos herunterladen",
    proof: "200.000+ Mitglieder weltweit",
    valuesEyebrow: "Wofür wir stehen",
    valuesTitle: "Unsere gemeinsamen Werte",
    values: [
      {
        title: "Wissenschaftlich fundiert",
        description:
          "Wir stehen hinter den Belegen. Impfungen gehören zu den größten Errungenschaften der modernen Medizin.",
      },
      {
        title: "Füreinander sorgen",
        description:
          "Impfen schützt nicht nur Sie selbst, sondern auch Menschen, die nicht geimpft werden können: Neugeborene, Ältere und Menschen mit geschwächtem Immunsystem.",
      },
      {
        title: "Globale Solidarität",
        description:
          "Herdenimmunität ist eine gemeinsame Leistung. Jede Impfung trägt zu einer gesünderen Welt bei.",
      },
      {
        title: "Inklusive Community",
        description:
          "Eltern, Fachkräfte im Gesundheitswesen, Reisende, Forschende: alle, die an Vorbeugung glauben, sind willkommen.",
      },
    ],
    storiesEyebrow: "Stimmen aus der Community",
    storiesTitle: "Echte Geschichten, echte Wirkung",
    testimonials: [
      {
        name: "Lena R.",
        role: "Mutter von drei Kindern",
        quote:
          "Mit Vaxy behalte ich die Impfungen meiner Kinder ganz einfach im Blick. Ich weiß, dass sie geschützt sind.",
      },
      {
        name: "Dr. Ayaan K.",
        role: "Hausarzt",
        quote:
          "Ich empfehle Vaxy allen Patientinnen und Patienten. Ein klares Impfprotokoll bedeutet bessere Versorgung und weniger verpasste Dosen.",
      },
      {
        name: "Sophie M.",
        role: "Vielreisende",
        quote:
          "Reisen in tropische Regionen ist viel entspannter, wenn ich genau weiß, welche Impfungen ich habe und wann sie ablaufen.",
      },
    ],
    movementTitle: "Werden Sie Teil der Bewegung",
    movementText:
      "Laden Sie Vaxy herunter, verwalten Sie Ihre Impfungen und helfen Sie mit, eine gesündere, besser geschützte Welt aufzubauen, Dosis für Dosis.",
  },
  app: {
    ...en.app,
    eyebrow: "Die App",
    title: "Dein digitaler Impfpass",
    intro:
      "Vaxy speichert alle Impfungen und Booster in einer App, auch für die ganze Familie. Erfasse Impfungen, erhalte Erinnerungen und buche Reiseimpfungen.",
    heroImageAlt:
      "Gesundes älteres Paar auf einem Dünenweg, mit der Vaxy-App und einer Erinnerung an die Grippeimpfung",
    insideEyebrow: "Was ist drin",
    insideTitle: "Alles in einer App",
    fullListEyebrow: "Alle Funktionen",
    fullListTitle: "Was Vaxy alles kann",
    downloadTitle: "Vaxy kostenlos herunterladen",
    downloadText:
      "Verfügbar für iOS und Android. Verwalte die Impfungen deiner Familie ab heute ganz unkompliziert.",
  },
  travel: {
    ...en.travel,
    eyebrow: "Reisegesundheit",
    title: "Sicher reisen, klug impfen",
    intro:
      "Jedes Reiseziel bringt eigene Gesundheitsrisiken mit. Erfahre vor der Abreise, welche Impfungen du brauchst, und speichere deine Nachweise in Vaxy.",
    tips: [
      {
        title: "Plan 4-6 Wochen im Voraus",
        desc: "Einige Impfungen erfordern mehrere Dosen über mehrere Wochen. Buche deinen Reiseimpftermin frühzeitig.",
      },
      {
        title: "Halte deinen Nachweis aktuell",
        desc: "Nimm deinen Impfnachweis mit. Einige Länder verlangen einen Nachweis an der Grenze.",
      },
      {
        title: "Einreisebestimmungen prüfen",
        desc: "Die Regeln ändern sich. Überprüfe immer die aktuellen Vorgaben bei offiziellen Gesundheitsbehörden.",
      },
      {
        title: "Zuerst Routineimpfungen",
        desc: "Stelle sicher, dass MMR, DTP, Grippe und COVID-19 aktuell sind, bevor du Reiseimpfungen hinzufügst.",
      },
    ],
    regionsTitle: "Impfungen nach Reiseziel",
    vaccineLabel: "Häufige Empfehlungen",
    allDestinationsTitle: "Alle Reiseziele",
    regions: [
      {
        slug: "zuidoost-azie",
        name: "Südostasien",
        vaccines: ["Hepatitis A", "Typhus", "Japanische Enzephalitis", "Tollwut"],
        note: "Malaria-Prophylaxe in ländlichen Gebieten empfohlen.",
      },
      {
        slug: "sub-sahara-afrika",
        name: "Subsahara-Afrika",
        vaccines: [
          "Gelbfieber",
          "Hepatitis A & B",
          "Typhus",
          "Meningokokken",
        ],
        note: "Gelbfieber-Zertifikat oft für die Einreise erforderlich.",
      },
      {
        slug: "zuid-amerika",
        name: "Südamerika",
        vaccines: ["Gelbfieber", "Hepatitis A", "Typhus", "Tollwut"],
        note: "Amazonas-Region erfordert zusätzliche Vorsichtsmaßnahmen.",
      },
      {
        slug: "midden-oosten",
        name: "Naher Osten",
        vaccines: ["Hepatitis A & B", "Typhus", "Meningokokken (Hadsch/Umra)"],
        note: "Meningokokken-Impfung für Pilger verpflichtend.",
      },
      {
        slug: "europa-noord-amerika",
        name: "Europa & Nordamerika",
        vaccines: ["Routineimpfungen aktuell", "Influenza", "COVID-19"],
        note: "Stellen Sie sicher, dass Routineimpfungen aktuell sind.",
      },
      {
        slug: "zuid-centraal-azie",
        name: "Süd- & Zentralasien",
        vaccines: ["Hepatitis A", "Typhus", "Polio-Auffrischung", "Tollwut"],
        note: "Polio-Auffrischung kann für Pakistan und Afghanistan erforderlich sein.",
      },
      {
        slug: "cariben",
        name: "Karibik & Mittelamerika",
        vaccines: ["Hepatitis A", "Typhus", "DTP"],
        note: "Dengue und andere durch Mücken übertragene Krankheiten sind häufig. Gelbfieber ist vor allem relevant bei Reisen über Südamerika.",
      },
      {
        slug: "oost-azie",
        name: "Ostasien",
        vaccines: ["DTP", "Hepatitis A", "Japanische Enzephalitis"],
        note: "Routineimpfungen reichen für die meisten Städtereisen. Japanische Enzephalitis gilt vor allem bei längeren Aufenthalten in ländlichen Gebieten.",
      },
      {
        slug: "oceanie",
        name: "Ozeanien",
        vaccines: ["DTP", "Hepatitis A", "Routineimpfungen aktuell"],
        note: "Australien und Neuseeland benötigen vor allem aktuelle Routineimpfungen. Auf vielen Inseln kommt Hepatitis-A-Schutz hinzu.",
      },
    ],
    destination: {
      title: "Impfungen {name}",
      lead: "Für {name} empfiehlt das LCR unter anderem {vaccines}.",
      leadEmpty: "Siehe unten die LCR-Empfehlung für eine Reise nach {name}.",
      source:
        "Quelle: Nationales Koordinationszentrum für Reisegesundheit (LCR)",
      sourceLink: "LCR-Seite öffnen",
      updated: "Zuletzt aktualisiert {date}",
      sections: {
        general: "Allgemeine Empfehlungen",
        conditional: "Abhängig von Ihrer Reise",
        other: "Weitere Hinweise",
      },
      faqTitle: "Häufig gestellte Fragen",
      faqRequiredQ: "Welche Impfungen sind für {name} verpflichtend?",
      faqRequiredYes:
        "Das LCR nennt für {name} Impfung(en), die für die Einreise verpflichtend sein können: {vaccines}. Prüfen Sie immer die aktuellen Einreisebestimmungen in einer Reiseimpfklinik.",
      faqRequiredNo:
        "Das LCR nennt für {name} keine verpflichtende Impfung für die Einreise. Empfohlen sind unter anderem {vaccines}.",
      faqRecommendedQ: "Welche Impfungen werden für {name} empfohlen?",
      faqRecommendedA:
        "Laut LCR gehören zu einer Reise nach {name} typischerweise: {vaccines}. Ob Sie sie brauchen, hängt von Ihrer Gesundheit, Reisedauer und Aktivitäten ab.",
      faqWhenQ: "Wann sollte ich Reiseimpfungen bekommen?",
      faqWhenA:
        "Vereinbaren Sie 4 bis 6 Wochen vor Abreise einen Termin in einer Reiseimpfklinik. Einige Impfungen erfordern mehr als eine Dosis.",
      siblingsTitle: "Weitere Länder in {region}",
      countriesTitle: "Länder in {region}",
      browseAll: "Alle Reiseziele",
      relatedGuide: "Leitfaden zu Reiseimpfungen lesen",
      relatedApp: "Erfahren Sie, was Vaxy kann",
    },
    ctaTitle: "Speichere alle Reiseimpfungen in Vaxy",
    ctaText:
      "Erhalte Erinnerungen, trage einen digitalen Nachweis bei dir und teile Daten mit Mitreisenden, alles in einer App.",
    cta: "Vaxy kostenlos laden",
  },
  blog: {
    eyebrow: "Artikel",
    title: "Gesundheit, Reisen und Impfungen klar erklärt",
    intro:
      "Praktische Ratgeber und Geschichten, damit du und deine Familie geschützt bleiben.",
    readArticle: "Artikel lesen",
    allArticles: "Alle Artikel",
    backToBlog: "Zurück zu den Artikeln",
    by: "Von",
  },
  contact: {
    eyebrow: "Kontakt",
    title: "Fragen? Wir helfen gern weiter",
    intro:
      "Haben Sie eine Frage zu Vaxy, Ihrem Konto oder dem digitalen Impfpass? Schreiben Sie uns, wir melden uns so schnell wie möglich.",
    generalLabel: "Allgemeine Fragen",
    legacyLabel: "Fragen zu Vaccinatieboekje",
    infoEmail: "info@vaxy.app",
    legacyEmail: "info@vaccinatieboekje.com",
  },
  deleteAccount: {
    eyebrow: "Konto",
    title: "Konto löschen",
    intro:
      "Du kannst dein Vaxy-Konto selbst in der App löschen oder eine Anfrage stellen, wenn das nicht möglich ist.",
    stepsTitle: "In der App löschen",
    steps: [
      "Öffne die Vaxy-App und melde dich an.",
      "Gehe zu Konto.",
      "Wähle Konto löschen.",
      "Bestätige deine Wahl. Dein Konto und die verknüpften personenbezogenen Daten werden dauerhaft gelöscht.",
    ],
    warning:
      "Das Löschen deines Kontos ist endgültig und kann nicht rückgängig gemacht werden. Alle mit deinem Konto verknüpften Daten werden ebenfalls gelöscht.",
    requestTitle: "Anfrage stellen",
    requestIntro:
      "Löschen über die App nicht möglich? Sende uns eine Anfrage. Verwende die E-Mail-Adresse deines Vaxy-Kontos, damit wir dich finden können.",
    emailLabel: "E-Mail-Adresse deines Kontos",
    emailPlaceholder: "name@beispiel.de",
    messageLabel: "Zusätzliche Angaben (optional)",
    messagePlaceholder: "Zum Beispiel: Ich habe keinen Zugang mehr zur App.",
    submitLabel: "Anfrage senden",
    requestHint:
      "Deine E-Mail-App öffnet sich mit einer vorausgefüllten Nachricht an info@vaxy.app. Wir bearbeiten Anfragen so schnell wie möglich.",
    supportEmail: "info@vaxy.app",
    mailSubject: "Antrag auf Kontolöschung",
    mailBodyIntro:
      "Ich beantrage, dass Vaxy mein Konto und die zugehörigen personenbezogenen Daten löscht.",
  },
};

const fr: PagesCopy = {
  ...en,
  about: {
    ...en.about,
    eyebrow: "À propos",
    title: "Bonjour, nous sommes Vaxy",
    intro:
      "Vaxy a été fondée par l'intensiviste Dr. Marco Goeijenbier et le développeur produit Pim Bruijnes. Nous aidons chacun à garder la maîtrise de sa santé, sans complication.",
    storyEyebrow: "Notre histoire",
    storyTitle: "D'une terrasse à Amsterdam à 200 000+ utilisateurs",
    teamEyebrow: "L'équipe",
    teamTitle: "Les personnes derrière Vaxy",
    linkedinLabel: "Profil LinkedIn",
    team: [
      {
        ...en.about.team[0],
        title: "Expert médical",
        role: "Cofondateur · Intensiviste",
      },
      { ...en.about.team[1], role: "Cofondateur · Produit et technologie" },
    ],
  },
  media: {
    ...en.media,
    title: "Vaxy dans les médias",
    intro: "Découvrez comment Vaxy change la gestion des vaccinations.",
    items: [
      {
        ...en.media.items[0],
        description:
          "Un rare hantavirus a causé plusieurs décès sur un navire néerlandais. Marco Goeijenbier explique la situation.",
        cta: "Lire l'article",
      },
      {
        ...en.media.items[1],
        description:
          "Fini de chercher votre carnet jaune. Vaxy rassemble toutes vos données vaccinales dans une app sécurisée, pour vous, vos enfants et vos vaccins de voyage.",
        cta: "Voir la vidéo",
      },
    ],
  },
  community: {
    ...en.community,
    eyebrow: "Communauté",
    title: "Unis pour la vaccination",
    intro:
      "Rejoignez une communauté grandissante qui croit en la science des vaccins et au pouvoir de la prévention. Ensemble, nous protégeons nos proches, nos familles et notre entourage.",
    cta: "Télécharger Vaxy gratuitement",
    proof: "200 000+ membres dans le monde",
    valuesEyebrow: "Ce que nous défendons",
    valuesTitle: "Nos valeurs communes",
    values: [
      {
        title: "Fondé sur la science",
        description:
          "Nous nous appuyons sur les preuves. Les vaccins comptent parmi les plus grandes avancées de la médecine moderne.",
      },
      {
        title: "Prendre soin des autres",
        description:
          "La vaccination protège non seulement vous, mais aussi ceux qui ne peuvent pas être vaccinés : les nouveau-nés, les personnes âgées et les personnes immunodéprimées.",
      },
      {
        title: "Solidarité mondiale",
        description:
          "L'immunité collective est un effort partagé. Chaque vaccination contribue à un monde plus sain.",
      },
      {
        title: "Communauté inclusive",
        description:
          "Parents, soignants, voyageurs, chercheurs : toute personne qui croit en la prévention est la bienvenue.",
      },
    ],
    storiesEyebrow: "Voix de la communauté",
    storiesTitle: "Des histoires vraies, un impact réel",
    testimonials: [
      {
        name: "Lena R.",
        role: "Mère de trois enfants",
        quote:
          "Vaxy rend le suivi des vaccins de mes enfants si simple. Je suis rassurée de savoir qu'ils sont protégés.",
      },
      {
        name: "Dr. Ayaan K.",
        role: "Médecin généraliste",
        quote:
          "Je recommande Vaxy à tous mes patients. Un carnet de vaccination clair, c'est de meilleurs soins et moins de doses manquées.",
      },
      {
        name: "Sophie M.",
        role: "Grande voyageuse",
        quote:
          "Voyager vers des destinations tropicales est bien moins stressant quand je sais exactement quels vaccins j'ai et quand ils expirent.",
      },
    ],
    movementTitle: "Rejoignez le mouvement",
    movementText:
      "Téléchargez Vaxy, suivez vos vaccins et contribuez à un monde plus sain et mieux protégé, dose après dose.",
  },
  app: {
    ...en.app,
    eyebrow: "L'application",
    title: "Votre carnet de vaccination numérique",
    intro:
      "Vaxy conserve tous vos vaccins et rappels dans une seule app, y compris ceux de votre famille. Enregistrez les injections, recevez des rappels et réservez vos vaccins de voyage.",
    heroImageAlt:
      "Couple âgé en bonne santé sur un sentier côtier, avec l'app Vaxy et un rappel de vaccin antigrippal",
    insideEyebrow: "Ce qu'elle contient",
    insideTitle: "Tout dans une seule app",
    fullListEyebrow: "Liste complète des fonctionnalités",
    fullListTitle: "Tout ce que Vaxy peut faire",
    downloadTitle: "Télécharger Vaxy gratuitement",
    downloadText:
      "Disponible sur iOS et Android. Gérez les vaccinations de votre famille dès aujourd'hui, sans complication.",
  },
  travel: {
    ...en.travel,
    eyebrow: "Santé voyage",
    title: "Voyagez en sécurité, vaccinez intelligemment",
    intro:
      "Chaque destination présente ses propres risques sanitaires. Découvrez les vaccins nécessaires avant de partir et conservez vos dossiers dans Vaxy.",
    tips: [
      {
        title: "Planifiez 4 à 6 semaines à l'avance",
        desc: "Certains vaccins nécessitent plusieurs doses étalées sur plusieurs semaines. Prenez votre rendez-vous santé voyage tôt.",
      },
      {
        title: "Gardez votre carnet à jour",
        desc: "Emportez votre certificat de vaccination. Certains pays exigent une preuve à la frontière.",
      },
      {
        title: "Vérifiez les conditions d'entrée",
        desc: "Les règles changent. Vérifiez toujours les dernières exigences auprès des autorités sanitaires officielles.",
      },
      {
        title: "D'abord les vaccins de routine",
        desc: "Assurez-vous que ROR, DTP, grippe et COVID-19 sont à jour avant d'ajouter des vaccins spécifiques au voyage.",
      },
    ],
    regionsTitle: "Vaccins par destination",
    vaccineLabel: "Recommandations fréquentes",
    allDestinationsTitle: "Toutes les destinations",
    regions: [
      {
        slug: "zuidoost-azie",
        name: "Asie du Sud-Est",
        vaccines: ["Hépatite A", "Typhoïde", "Encéphalite japonaise", "Rage"],
        note: "Prophylaxie antipaludéenne recommandée en zone rurale.",
      },
      {
        slug: "sub-sahara-afrika",
        name: "Afrique subsaharienne",
        vaccines: [
          "Fièvre jaune",
          "Hépatite A et B",
          "Typhoïde",
          "Méningocoque",
        ],
        note: "Certificat de fièvre jaune souvent exigé à l'entrée.",
      },
      {
        slug: "zuid-amerika",
        name: "Amérique du Sud",
        vaccines: ["Fièvre jaune", "Hépatite A", "Typhoïde", "Rage"],
        note: "La région amazonienne exige des précautions supplémentaires.",
      },
      {
        slug: "midden-oosten",
        name: "Moyen-Orient",
        vaccines: ["Hépatite A et B", "Typhoïde", "Méningocoque (Hajj/Omra)"],
        note: "Vaccin méningocoque obligatoire pour les pèlerins.",
      },
      {
        slug: "europa-noord-amerika",
        name: "Europe et Amérique du Nord",
        vaccines: ["Vaccins de routine à jour", "Grippe", "COVID-19"],
        note: "Assurez-vous que vos vaccinations de routine sont à jour.",
      },
      {
        slug: "zuid-centraal-azie",
        name: "Asie du Sud et centrale",
        vaccines: ["Hépatite A", "Typhoïde", "Rappel polio", "Rage"],
        note: "Un rappel polio peut être requis pour le Pakistan et l'Afghanistan.",
      },
      {
        slug: "cariben",
        name: "Caraïbes et Amérique centrale",
        vaccines: ["Hépatite A", "Typhoïde", "DTP"],
        note: "La dengue et d'autres maladies transmises par les moustiques sont fréquentes. La fièvre jaune surtout si vous transitez par l'Amérique du Sud.",
      },
      {
        slug: "oost-azie",
        name: "Asie de l'Est",
        vaccines: ["DTP", "Hépatite A", "Encéphalite japonaise"],
        note: "Les vaccins de routine suffisent pour la plupart des séjours en ville. L'encéphalite japonaise surtout pour les longs séjours en zone rurale.",
      },
      {
        slug: "oceanie",
        name: "Océanie",
        vaccines: ["DTP", "Hépatite A", "Vaccins de routine à jour"],
        note: "L'Australie et la Nouvelle-Zélande demandent surtout des vaccins de routine à jour. De nombreuses îles ajoutent une protection contre l'hépatite A.",
      },
    ],
    destination: {
      title: "Vaccinations {name}",
      lead: "Pour {name}, le LCR recommande entre autres {vaccines}.",
      leadEmpty: "Consultez ci-dessous l'avis LCR pour un voyage vers {name}.",
      source:
        "Source : Centre national de coordination pour la santé des voyageurs (LCR)",
      sourceLink: "Ouvrir la page LCR",
      updated: "Dernière mise à jour {date}",
      sections: {
        general: "Conseils généraux",
        conditional: "Selon votre voyage",
        other: "Autres points d'attention",
      },
      faqTitle: "Questions fréquentes",
      faqRequiredQ: "Quels vaccins sont obligatoires pour {name} ?",
      faqRequiredYes:
        "Le LCR mentionne pour {name} des vaccins qui peuvent être obligatoires à l'entrée : {vaccines}. Vérifiez toujours les dernières règles d'entrée auprès d'un centre de vaccination voyage.",
      faqRequiredNo:
        "Le LCR ne mentionne pas de vaccin obligatoire à l'entrée pour {name}. Les vaccins recommandés incluent {vaccines}.",
      faqRecommendedQ: "Quels vaccins sont recommandés pour {name} ?",
      faqRecommendedA:
        "Selon le LCR, un voyage vers {name} comprend généralement : {vaccines}. Selon votre santé, la durée du séjour et vos activités, tous ne seront pas nécessaires.",
      faqWhenQ: "Quand dois-je me faire vacciner avant un voyage ?",
      faqWhenA:
        "Prenez rendez-vous dans un centre de vaccination voyage 4 à 6 semaines avant le départ. Certains vaccins nécessitent plusieurs doses.",
      siblingsTitle: "Autres pays en {region}",
      countriesTitle: "Pays en {region}",
      browseAll: "Toutes les destinations",
      relatedGuide: "Lire le guide sur les vaccins de voyage",
      relatedApp: "Découvrir ce que Vaxy peut faire",
    },
    ctaTitle: "Conservez tous vos vaccins de voyage dans Vaxy",
    ctaText:
      "Recevez des rappels, gardez un certificat numérique avec vous et partagez vos données avec vos compagnons de voyage.",
    cta: "Obtenir Vaxy gratuitement",
  },
  blog: {
    eyebrow: "Articles",
    title: "Santé, voyage et vaccins expliqués simplement",
    intro: "Des guides pratiques et des histoires pour protéger votre famille.",
    readArticle: "Lire l'article",
    allArticles: "Tous les articles",
    backToBlog: "Retour aux articles",
    by: "Par",
  },
  contact: {
    eyebrow: "Contact",
    title: "Des questions ? Nous sommes là pour vous aider",
    intro:
      "Une question sur Vaxy, votre compte ou le carnet vaccinal numérique ? Envoyez-nous un e-mail, nous répondrons dès que possible.",
    generalLabel: "Questions générales",
    legacyLabel: "Questions sur Vaccinatieboekje",
    infoEmail: "info@vaxy.app",
    legacyEmail: "info@vaccinatieboekje.com",
  },
  deleteAccount: {
    eyebrow: "Compte",
    title: "Supprimer mon compte",
    intro:
      "Vous pouvez supprimer votre compte Vaxy dans l'application, ou envoyer une demande si cela n'est pas possible.",
    stepsTitle: "Supprimer dans l'application",
    steps: [
      "Ouvrez l'application Vaxy et connectez-vous.",
      "Allez dans Compte.",
      "Choisissez Supprimer le compte.",
      "Confirmez votre choix. Votre compte et les données personnelles associées seront définitivement supprimés.",
    ],
    warning:
      "La suppression de votre compte est définitive et ne peut pas être annulée. Toutes les données liées à votre compte seront également supprimées.",
    requestTitle: "Envoyer une demande",
    requestIntro:
      "Impossible via l'application ? Envoyez-nous une demande. Utilisez l'adresse e-mail de votre compte Vaxy pour que nous puissions vous retrouver.",
    emailLabel: "Adresse e-mail de votre compte",
    emailPlaceholder: "nom@exemple.fr",
    messageLabel: "Précisions (facultatif)",
    messagePlaceholder: "Par exemple : je n'ai plus accès à l'application.",
    submitLabel: "Envoyer la demande",
    requestHint:
      "Votre application e-mail s'ouvre avec un message prérempli adressé à info@vaxy.app. Nous traitons les demandes dès que possible.",
    supportEmail: "info@vaxy.app",
    mailSubject: "Demande de suppression de compte",
    mailBodyIntro:
      "Je demande à Vaxy de supprimer mon compte et les données personnelles associées.",
  },
};

const es: PagesCopy = {
  ...en,
  about: {
    ...en.about,
    eyebrow: "Sobre nosotros",
    title: "Hola, somos Vaxy",
    intro:
      "Vaxy fue fundada por el intensivista Dr. Marco Goeijenbier y el desarrollador de producto Pim Bruijnes. Ayudamos a las personas a mantener el control de su salud sin complicaciones.",
    storyEyebrow: "Nuestra historia",
    storyTitle: "De una azotea en Ámsterdam a 200.000+ usuarios",
    teamEyebrow: "El equipo",
    teamTitle: "Las personas detrás de Vaxy",
    linkedinLabel: "Perfil de LinkedIn",
    team: [
      {
        ...en.about.team[0],
        title: "Experto médico",
        role: "Cofundador · Intensivista",
      },
      { ...en.about.team[1], role: "Cofundador · Producto y tecnología" },
    ],
  },
  media: {
    ...en.media,
    title: "Vaxy en los medios",
    intro: "Mira cómo Vaxy está cambiando la forma de gestionar las vacunas.",
    items: [
      {
        ...en.media.items[0],
        description:
          "Un raro hantavirus causó varias muertes en un barco neerlandés. Marco Goeijenbier explica lo ocurrido.",
        cta: "Leer el artículo",
      },
      {
        ...en.media.items[1],
        description:
          "Nunca más buscar la cartilla amarilla. Vaxy reúne todos tus datos de vacunación en una app segura para ti, tus hijos y tus vacunas de viaje.",
        cta: "Ver el vídeo",
      },
    ],
  },
  community: {
    ...en.community,
    eyebrow: "Comunidad",
    title: "Unidos por la vacunación",
    intro:
      "Únete a una comunidad creciente de personas que creen en la ciencia de las vacunas y en el poder de la prevención. Juntos protegemos a los nuestros, a nuestras familias y a nuestro entorno.",
    cta: "Descargar Vaxy gratis",
    proof: "200.000+ miembros en todo el mundo",
    valuesEyebrow: "Lo que defendemos",
    valuesTitle: "Nuestros valores compartidos",
    values: [
      {
        title: "Basado en la ciencia",
        description:
          "Nos apoyamos en la evidencia. Las vacunas son uno de los mayores logros de la medicina moderna.",
      },
      {
        title: "Cuidar de los demás",
        description:
          "La vacunación protege no solo a ti, sino también a quienes no pueden vacunarse: recién nacidos, personas mayores y personas inmunodeprimidas.",
      },
      {
        title: "Solidaridad global",
        description:
          "La inmunidad colectiva es un esfuerzo compartido. Cada vacuna contribuye a un mundo más sano.",
      },
      {
        title: "Comunidad inclusiva",
        description:
          "Padres, profesionales sanitarios, viajeros, investigadores: todos los que creen en la prevención son bienvenidos.",
      },
    ],
    storiesEyebrow: "Voces de la comunidad",
    storiesTitle: "Historias reales, impacto real",
    testimonials: [
      {
        name: "Lena R.",
        role: "Madre de tres hijos",
        quote:
          "Vaxy hace que sea muy fácil llevar el control de las vacunas de mis hijos. Me tranquiliza saber que están protegidos.",
      },
      {
        name: "Dr. Ayaan K.",
        role: "Médico de familia",
        quote:
          "Recomiendo Vaxy a todos mis pacientes. Tener un registro claro de vacunación significa mejor atención y menos dosis perdidas.",
      },
      {
        name: "Sophie M.",
        role: "Viajera frecuente",
        quote:
          "Viajar a destinos tropicales es mucho menos estresante cuando sé exactamente qué vacunas tengo y cuándo caducan.",
      },
    ],
    movementTitle: "Forma parte del movimiento",
    movementText:
      "Descarga Vaxy, controla tus vacunas y ayuda a construir un mundo más sano y protegido, dosis a dosis.",
  },
  app: {
    ...en.app,
    eyebrow: "La app",
    title: "Tu cartilla de vacunación digital",
    intro:
      "Vaxy guarda todas tus vacunas y refuerzos en una sola app, también los de toda tu familia. Registra dosis, recibe recordatorios y reserva vacunas de viaje.",
    heroImageAlt:
      "Pareja mayor saludable en un camino costero, con la app Vaxy y un recordatorio de la vacuna de la gripe",
    insideEyebrow: "Qué incluye",
    insideTitle: "Todo en una app",
    fullListEyebrow: "Lista completa de funciones",
    fullListTitle: "Todo lo que puede hacer Vaxy",
    downloadTitle: "Descarga Vaxy gratis",
    downloadText:
      "Disponible en iOS y Android. Empieza hoy a gestionar las vacunas de tu familia sin complicaciones.",
  },
  travel: {
    ...en.travel,
    eyebrow: "Salud en viajes",
    title: "Viaja seguro, vacúnate con cabeza",
    intro:
      "Cada destino tiene sus propios riesgos sanitarios. Descubre qué vacunas necesitas antes de salir y guarda tus registros en Vaxy.",
    tips: [
      {
        title: "Planifica con 4-6 semanas de antelación",
        desc: "Algunas vacunas requieren varias dosis repartidas en semanas. Reserva tu cita de salud de viaje con tiempo.",
      },
      {
        title: "Mantén tu registro al día",
        desc: "Lleva tu certificado de vacunación. Algunos países exigen prueba en la frontera.",
      },
      {
        title: "Consulta los requisitos de entrada",
        desc: "Las normas cambian. Comprueba siempre las últimas exigencias con las autoridades sanitarias oficiales.",
      },
      {
        title: "Primero las vacunas de rutina",
        desc: "Asegúrate de que sarampión-rubeola-paperas, difteria-tétanos-tosferina, gripe y COVID-19 estén al día antes de añadir vacunas de viaje.",
      },
    ],
    regionsTitle: "Vacunas por destino",
    vaccineLabel: "Recomendaciones habituales",
    allDestinationsTitle: "Todos los destinos",
    regions: [
      {
        slug: "zuidoost-azie",
        name: "Sudeste asiático",
        vaccines: ["Hepatitis A", "Tifoidea", "Encefalitis japonesa", "Rabia"],
        note: "Profilaxis antimalárica recomendada en zonas rurales.",
      },
      {
        slug: "sub-sahara-afrika",
        name: "África subsahariana",
        vaccines: [
          "Fiebre amarilla",
          "Hepatitis A y B",
          "Tifoidea",
          "Meningococo",
        ],
        note: "Certificado de fiebre amarilla a menudo exigido para entrar.",
      },
      {
        slug: "zuid-amerika",
        name: "Sudamérica",
        vaccines: ["Fiebre amarilla", "Hepatitis A", "Tifoidea", "Rabia"],
        note: "La región amazónica requiere precauciones adicionales.",
      },
      {
        slug: "midden-oosten",
        name: "Oriente Medio",
        vaccines: ["Hepatitis A y B", "Tifoidea", "Meningococo (Hajj/Umrah)"],
        note: "Vacuna meningocócica obligatoria para peregrinos.",
      },
      {
        slug: "europa-noord-amerika",
        name: "Europa y Norteamérica",
        vaccines: ["Vacunas de rutina al día", "Gripe", "COVID-19"],
        note: "Asegúrate de que las vacunas de rutina estén al día.",
      },
      {
        slug: "zuid-centraal-azie",
        name: "Asia meridional y central",
        vaccines: ["Hepatitis A", "Tifoidea", "Refuerzo de polio", "Rabia"],
        note: "Puede exigirse refuerzo de polio para Pakistán y Afganistán.",
      },
      {
        slug: "cariben",
        name: "Caribe y Centroamérica",
        vaccines: ["Hepatitis A", "Tifoidea", "DTP"],
        note: "El dengue y otras enfermedades transmitidas por mosquitos son frecuentes. La fiebre amarilla importa sobre todo si viajas vía Sudamérica.",
      },
      {
        slug: "oost-azie",
        name: "Asia oriental",
        vaccines: ["DTP", "Hepatitis A", "Encefalitis japonesa"],
        note: "Las vacunas de rutina cubren la mayoría de viajes urbanos. La encefalitis japonesa aplica sobre todo a estancias largas en zonas rurales.",
      },
      {
        slug: "oceanie",
        name: "Oceanía",
        vaccines: ["DTP", "Hepatitis A", "Vacunas de rutina al día"],
        note: "Australia y Nueva Zelanda requieren sobre todo vacunas de rutina al día. En muchas islas se añade protección contra hepatitis A.",
      },
    ],
    destination: {
      title: "Vacunas {name}",
      lead: "Para {name}, el LCR recomienda entre otras {vaccines}.",
      leadEmpty: "Consulta abajo el consejo del LCR para viajar a {name}.",
      source:
        "Fuente: Centro Nacional de Coordinación para la Salud del Viajero (LCR)",
      sourceLink: "Abrir la página del LCR",
      updated: "Última actualización {date}",
      sections: {
        general: "Consejo general",
        conditional: "Según tu viaje",
        other: "Otros puntos a tener en cuenta",
      },
      faqTitle: "Preguntas frecuentes",
      faqRequiredQ: "¿Qué vacunas son obligatorias para {name}?",
      faqRequiredYes:
        "El LCR menciona para {name} vacuna(s) que pueden ser obligatorias para entrar: {vaccines}. Comprueba siempre las normas de entrada más recientes en una clínica de vacunación de viaje.",
      faqRequiredNo:
        "El LCR no menciona una vacuna obligatoria para entrar a {name}. Las vacunas recomendadas incluyen {vaccines}.",
      faqRecommendedQ: "¿Qué vacunas se recomiendan para {name}?",
      faqRecommendedA:
        "Según el LCR, un viaje a {name} suele incluir: {vaccines}. Si las necesitas depende de tu salud, la duración del viaje y tus actividades.",
      faqWhenQ: "¿Cuándo debo ponerme las vacunas de viaje?",
      faqWhenA:
        "Pide cita en una clínica de vacunación de viaje 4 a 6 semanas antes de la salida. Algunas vacunas requieren más de una dosis.",
      siblingsTitle: "Otros países en {region}",
      countriesTitle: "Países en {region}",
      browseAll: "Todos los destinos",
      relatedGuide: "Leer la guía de vacunas de viaje",
      relatedApp: "Descubre qué puede hacer Vaxy",
    },
    ctaTitle: "Guarda todas tus vacunas de viaje en Vaxy",
    ctaText:
      "Recibe recordatorios, lleva un certificado digital y comparte registros con tus compañeros de viaje, todo en una app.",
    cta: "Obtener Vaxy gratis",
  },
  blog: {
    eyebrow: "Artículos",
    title: "Salud, viajes y vacunas explicados con claridad",
    intro: "Guías prácticas e historias para mantener protegida a tu familia.",
    readArticle: "Leer artículo",
    allArticles: "Todos los artículos",
    backToBlog: "Volver a los artículos",
    by: "Por",
  },
  contact: {
    eyebrow: "Contacto",
    title: "¿Preguntas? Nos encantará ayudarte",
    intro:
      "¿Tienes una pregunta sobre Vaxy, tu cuenta o la cartilla digital de vacunación? Escríbenos y responderemos lo antes posible.",
    generalLabel: "Preguntas generales",
    legacyLabel: "Preguntas sobre Vaccinatieboekje",
    infoEmail: "info@vaxy.app",
    legacyEmail: "info@vaccinatieboekje.com",
  },
  deleteAccount: {
    eyebrow: "Cuenta",
    title: "Eliminar cuenta",
    intro:
      "Puedes eliminar tu cuenta de Vaxy en la app, o enviar una solicitud si no es posible.",
    stepsTitle: "Eliminar en la app",
    steps: [
      "Abre la app Vaxy e inicia sesión.",
      "Ve a Cuenta.",
      "Elige Eliminar cuenta.",
      "Confirma tu elección. Tu cuenta y los datos personales asociados se eliminarán de forma permanente.",
    ],
    warning:
      "Eliminar tu cuenta es permanente y no se puede deshacer. Todos los datos vinculados a tu cuenta también se eliminarán.",
    requestTitle: "Enviar una solicitud",
    requestIntro:
      "¿No puedes hacerlo en la app? Envíanos una solicitud. Usa el correo de tu cuenta Vaxy para que podamos encontrarte.",
    emailLabel: "Correo electrónico de tu cuenta",
    emailPlaceholder: "nombre@ejemplo.com",
    messageLabel: "Detalles adicionales (opcional)",
    messagePlaceholder: "Por ejemplo: ya no tengo acceso a la app.",
    submitLabel: "Enviar solicitud",
    requestHint:
      "Se abre tu app de correo con un mensaje precargado a info@vaxy.app. Procesamos las solicitudes lo antes posible.",
    supportEmail: "info@vaxy.app",
    mailSubject: "Solicitud de eliminación de cuenta",
    mailBodyIntro:
      "Solicito a Vaxy que elimine mi cuenta y los datos personales relacionados.",
  },
};

export const pages: Record<Locale, PagesCopy> = copy(nl, en, de, fr, es);
